import React, { useEffect, useState, useContext, useCallback } from "react";
import {
    Button,
    Classes,
    Dialog,
    Intent,
    FormGroup,
    InputGroup,
    TextArea,
    MenuItem,
    Tag,
    Card,
    H5
} from "@blueprintjs/core";
import { ItemPredicate, ItemRenderer, Select } from "@blueprintjs/select";
import { Company } from "src/models/Companies";
import { NewTemplateDialogContext, NewTemplateInstructionsContext } from "../../utils/context";
import { useQuery } from "@apollo/client";

import { UserProject } from "src/models/UserProjects";
import InstructionPreviewDialog from "./InstructionPreviewDialog";
import { createTemplate, Dictionary, insertInstruction, validateAllInputsMapped } from "src/utils/utils";
import NewTemplateInstructions from "./NewTemplateInstructions";
import { useCreateTemplate } from "src/operations/mutations/createTemplate";
import { CreationToaster } from "../Toaster";

import "./NewTemplateDialog.scss";
import { useProjects } from "src/hooks/useProjects";
import { GET_COMPANIES } from "src/operations/queries/getCompanies";
import SourceDialog from "./InputSourceDialog";
import { ListState } from "src/models/TemplateCreationList";
import { UserInputs } from "src/models/UserInputs";

const CompanySelect = Select.ofType<Company>();

const filterCompany: ItemPredicate<Company> = (query, company) => {
    return company.name.toLowerCase().indexOf(query.toLowerCase()) >= 0;
};
const renderCompany: ItemRenderer<Company> = (company, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
        return null;
    }
    return <MenuItem active={modifiers.active} key={company.id} onClick={handleClick} text={company.name} />;
};

const defaultCompany = { __typename: "Company", id: "", name: "" };
export type TemplateProject = UserProject & {
    visible: boolean;
};

export default function NewTemplateDialog() {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [open, setOpen] = useContext(NewTemplateDialogContext);
    const [previewInstruction, setPreviewInstruction] = useState<undefined | TemplateProject>();
    const [availableProjects, setAvailableProjects] = useState<TemplateProject[]>([]);
    const [templateInstructions, setTemplateInstructions] = useContext(NewTemplateInstructionsContext);
    const [selectedCompany, setSelectedCompany] = useState(defaultCompany);
    const [masterList, setMasterList] = useState<Dictionary<ListState>>({});
    const [processInputs, setProcessInputs] = useState<UserInputs>({});

    const { data: companies, loading, error } = useQuery<{ companies: Company[] }>(GET_COMPANIES, {
        errorPolicy: "all"
    });
    const [projects] = useProjects();
    const { mutate: addTemplate } = useCreateTemplate();

    useEffect(() => {
        if (projects && selectedCompany.id) {
            const p = projects.filter(p => p.companySysId === selectedCompany.id).map(p => ({ ...p, visible: true }));
            setAvailableProjects(p);
        }
    }, [projects, selectedCompany.id]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setSubmitting(false);
        setSelectedCompany(defaultCompany);
        setTemplateInstructions([]);
        setName("");
        setDescription("");
        setMasterList({});
        setProcessInputs({});
    }, [setOpen, setTemplateInstructions]);

    const handleCreate = useCallback(() => {
        const [allInputsMapped, errorMsg] = validateAllInputsMapped(masterList, templateInstructions, processInputs);
        if (!allInputsMapped) {
            CreationToaster.show({
                message: `Could not create template. ${errorMsg}`,
                intent: Intent.WARNING
            });
            return;
        }
        const template = createTemplate(
            selectedCompany.id,
            name,
            description,
            templateInstructions,
            processInputs,
            masterList,
            availableProjects
        );

        setSubmitting(true);
        addTemplate({ variables: { input: template } })
            .then(res => {
                if (!res.errors) {
                    CreationToaster.show({
                        message: `Successfully created template: ${res.data?.createProcessTemplate?.name}`,
                        intent: Intent.SUCCESS
                    });
                } else {
                    CreationToaster.show({ message: `Failed to Create Template`, intent: Intent.DANGER });
                }
            })
            .catch(e => {
                CreationToaster.show({ message: `Failed to Create Template`, intent: Intent.DANGER });
            })
            .finally(() => setSubmitting(false));
    }, [
        addTemplate,
        availableProjects,
        description,
        masterList,
        name,
        processInputs,
        selectedCompany.id,
        templateInstructions
    ]);

    return (
        // enforceFocus has to be false for CompanySelect filtering to work, otherwise the Dialog will steal focus from the InputGroup
        <>
            <SourceDialog
                availableInstructions={availableProjects}
                processInputs={processInputs}
                setProcessInputs={setProcessInputs}
                masterList={masterList}
                setMasterList={setMasterList}
            />
            <Dialog enforceFocus={false} isOpen={open} icon="annotation" onClose={handleClose} title="New Template">
                <div className={Classes.DIALOG_BODY}>
                    <div className="form-group">
                        <FormGroup
                            inline={false}
                            label="Template Name"
                            labelFor="text-input"
                            labelInfo="*"
                            intent="danger"
                        >
                            <InputGroup id="template" value={name} onChange={e => setName(e.target.value)} />
                        </FormGroup>
                        <FormGroup
                            inline={false}
                            label="Description"
                            labelFor="text-input"
                            labelInfo="*"
                            intent="danger"
                        >
                            <TextArea fill value={description} onChange={e => setDescription(e.target.value)} />
                        </FormGroup>
                        <FormGroup
                            inline={false}
                            label="Company"
                            labelFor="text-input"
                            labelInfo="*"
                            disabled={!!error || !!loading}
                        >
                            <CompanySelect
                                itemPredicate={filterCompany}
                                itemRenderer={renderCompany}
                                items={companies ? companies.companies : []}
                                onItemSelect={c => {
                                    if (selectedCompany.id !== c.id) {
                                        setTemplateInstructions([]);
                                    }
                                    setSelectedCompany(c);
                                }}
                                noResults={<MenuItem disabled={true} text="No results." />}
                                popoverProps={{ minimal: true, popoverClassName: "custom-select" }}
                            >
                                <Button
                                    loading={loading}
                                    text={selectedCompany.name !== "" ? selectedCompany.name : "Select Company"}
                                    icon="office"
                                    rightIcon="caret-down"
                                />
                            </CompanySelect>
                        </FormGroup>
                        {previewInstruction && (
                            <InstructionPreviewDialog
                                userProject={previewInstruction}
                                close={() => setPreviewInstruction(undefined)}
                            />
                        )}
                    </div>

                    {(availableProjects.length > 0 || templateInstructions.length > 0) && (
                        <div className="instructions">
                            <Card className="instructions-card">
                                <H5>Available Instructions</H5>

                                {availableProjects
                                    ?.filter(i => i.visible)
                                    .map(i => (
                                        <div key={`div_${i.id}`} className="instruction-line">
                                            <Tag className="instruction" key={i.id}>
                                                {i.name}
                                            </Tag>

                                            <Button
                                                key={`eye_btn_${i.id}`}
                                                icon="eye-open"
                                                intent="success"
                                                onClick={() => {
                                                    setPreviewInstruction(i);
                                                }}
                                            />
                                            <Button
                                                key={`arrow_btn_${i.id}`}
                                                icon="add"
                                                intent="primary"
                                                onClick={() => {
                                                    setTemplateInstructions(insertInstruction(i, templateInstructions));
                                                    const availableCopy = [...availableProjects];
                                                    const idx = availableProjects.indexOf(i);
                                                    availableCopy[idx].visible = false;
                                                    setAvailableProjects(availableCopy);
                                                }}
                                            />
                                        </div>
                                    ))}
                            </Card>
                            <Card className="instructions-card">
                                <H5>Added Instructions</H5>
                                {templateInstructions.length > 0 && (
                                    <NewTemplateInstructions
                                        availableInstructions={availableProjects}
                                        setAvailableInstructions={setAvailableProjects}
                                        masterList={masterList}
                                        setMasterList={setMasterList}
                                    />
                                )}
                            </Card>
                        </div>
                    )}
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                    <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                        <Button
                            text="Create"
                            type="submit"
                            intent={Intent.PRIMARY}
                            loading={submitting}
                            onClick={handleCreate}
                            disabled={!isValid(selectedCompany.id, name, description)}
                        />
                        <Button text="Close" onClick={handleClose} />
                    </div>
                </div>
            </Dialog>
        </>
    );
}

function isValid(companySysId: string, name: string, description: string) {
    return !!companySysId && !!name && !!description;
}
